import { Company } from "@/services/company-service";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, CheckCircle, XCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

interface CompaniesTableProps {
  companies: Company[];
  loading?: boolean;
  onEdit: (company: Company) => void;
  onDelete: (company: Company) => void;
  onActivate: (company: Company) => void;
  onInactivate: (company: Company) => void;
}

export function CompaniesTable({
  companies,
  loading,
  onEdit,
  onDelete,
  onActivate,
  onInactivate,
}: CompaniesTableProps) {
  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (companies.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Nenhuma empresa encontrada.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50 hover:bg-muted/50">
            <TableHead className="th">Nome</TableHead>
            <TableHead className="th">Status</TableHead>
            <TableHead className="th text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {companies.map((company) => (
            <TableRow key={company.id} className="hover:bg-muted/30 transition-colors">
              <TableCell className="td font-medium">{company.name}</TableCell>
              <TableCell className="td">
                <Badge variant={company.status === 1 ? "default" : "secondary"} className="text-xs py-0">
                  {company.status === 1 ? "Ativo" : "Inativo"}
                </Badge>
              </TableCell>
              <TableCell className="td text-right">
                <div className="flex gap-1 justify-end">
                  <Button variant="ghost" size="icon" onClick={() => onEdit(company)} title="Editar" className="h-7 w-7">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => onDelete(company)} title="Excluir" className="h-7 w-7">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                  {company.status === 1 ? (
                    <Button variant="ghost" size="icon" onClick={() => onInactivate(company)} title="Inativar" className="h-7 w-7">
                      <XCircle className="h-3.5 w-3.5 text-orange-500" />
                    </Button>
                  ) : (
                    <Button variant="ghost" size="icon" onClick={() => onActivate(company)} title="Ativar" className="h-7 w-7">
                      <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
